import { middlewareGateway } from '~/gateway/middleware.gateway'

interface CustomerCIDResponse {
  cid: string
}

const MAX_RETRIES = 3
const INITIAL_RETRY_DELAY = 3000
const TIMEOUT = 5000

export const fetchCustomerCID = async (): Promise<CustomerCIDResponse> => {
  const attemptFetch = async (attempt = 0): Promise<CustomerCIDResponse> => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT)

    try {
      const response = await middlewareGateway<CustomerCIDResponse>('/next-gen/mhj/customer/cid', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        credentials: 'include',
      })

      if (!response) {
        throw new Error(`Failed to fetch CID: ${response}`)
      }

      return response as CustomerCIDResponse
    } catch (error) {
      /* NOTE:
       * Error responses from the Middleware (e.g. error.type === 'CUSTOMER_CID_NOT_FOUND')
       * will not be surfaced to the response object. Instead the 404 error response will
       * show as an empty response payload in the browser network tab.
       */
      if (attempt < MAX_RETRIES) {
        const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt)
        await new Promise((resolve) => setTimeout(resolve, delay))
        return attemptFetch(attempt + 1)
      }
      throw error
    } finally {
      clearTimeout(timeoutId)
    }
  }

  // Fire and forget pattern
  return new Promise((resolve, reject) => {
    queueMicrotask(async () => {
      try {
        const result = await attemptFetch()
        resolve(result)
      } catch (error) {
        reject(error)
      }
    })
  })
}

export const verifyBrazeEdmParam = (token: string): Promise<CustomerCIDResponse> => {
  return new Promise((resolve, reject) => {
    queueMicrotask(async () => {
      try {
        const response = await middlewareGateway<CustomerCIDResponse>(`/next-gen/mhj/braze/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        })

        if (!response) {
          throw new Error(`Failed to validate CID token: ${response}`)
        }
        resolve(response)
      } catch (error) {
        reject(error)
      }
    })
  })
}
